import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { communityApi } from '../../api/community'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import { FiHeart } from 'react-icons/fi'
import { AiFillHeart } from 'react-icons/ai'
import { FiMessageCircle, FiShare2 } from 'react-icons/fi'
import './Community.css'

const Community = () => {
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['community-posts'],
    queryFn: () => communityApi.getPosts(),
    retry: false,
  })

  const reactMutation = useMutation({
    mutationFn: ({ postId, data }) => communityApi.reactToPost(postId, data),
    onSuccess: (response, variables) => {
      // Update the post's liked status from API response
      queryClient.setQueryData(['community-posts'], (oldData) => {
        if (!oldData) return oldData
        const postsData = oldData?.data
        const posts = postsData?.data || postsData?.posts || oldData?.posts || []
        const responseData = response?.data || {}
        
        const updatedPosts = posts.map((post) => {
          if (post.id === variables.postId) {
            return {
              ...post,
              is_liked: responseData.is_liked !== undefined ? responseData.is_liked : !post.is_liked,
              likes_count: responseData.like_count !== undefined ? responseData.like_count : (post.likes_count || 0),
            }
          }
          return post
        })
        
        // Return updated structure based on original structure
        if (postsData?.data) {
          return {
            ...oldData,
            data: {
              ...postsData,
              data: updatedPosts,
            },
          }
        } else if (postsData?.posts) {
          return {
            ...oldData,
            data: {
              ...postsData,
              posts: updatedPosts,
            },
          }
        } else {
          return {
            ...oldData,
            posts: updatedPosts,
          }
        }
      })
    },
  })

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  // Handle different response structures - API returns paginated data
  const postsData = data?.data
  const posts = postsData?.data || postsData?.posts || data?.posts || []

  const handleReact = (postId) => {
    if (!isAuthenticated) {
      alert('يجب تسجيل الدخول للمشاركة')
      return
    }
    reactMutation.mutate({ postId, data: { type: 'like' } })
  }

  return (
    <div className="community-page bg-gray-900 min-h-screen">
      <Header />
      <main className="community-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="community-header">
            <h1 className="page-title text-white">المجتمع</h1>
            {isAuthenticated && (
              <Link to="/community/create">
                <Button variant="primary">إنشاء منشور</Button>
              </Link>
            )}
          </div>

          <div className="posts-list">
            {posts.map((post) => {
              // Check if post is liked
              // API returns is_liked (boolean) - see CommunityController line 51
              // Primary: Check is_liked field (strict check for true)
              // Fallback: Check if current user is in likes array
              const isLiked = post.is_liked === true || 
                             post.is_liked === 'true' || 
                             post.is_liked === 1 ||
                             (user && post.likes && Array.isArray(post.likes) && 
                              post.likes.some(like => {
                                const likeUserId = like.user_id || like.user?.id
                                return likeUserId && likeUserId.toString() === user.id.toString()
                              }))
              
              // Debug logging - check browser console to see what the API returns
              console.log(`Post ${post.id}:`, {
                is_liked: post.is_liked,
                is_liked_type: typeof post.is_liked,
                is_liked_strict_true: post.is_liked === true,
                likes_count: post.likes_count,
                likes_array: post.likes,
                user_id: user?.id,
                calculated_isLiked: isLiked
              })
              
              return (
              <Card key={post.id} className="post-card">
                <div className="post-header">
                  <div className="post-author">
                    {post.user?.avatar ? (
                      <img src={getStorageUrl(post.user.avatar)} alt={post.user.name} className="author-avatar" />
                    ) : (
                      <div className="author-avatar-placeholder">
                        {post.user?.name?.[0] || 'U'}
                      </div>
                    )}
                    <div>
                      <h3 className="author-name">{post.user?.name || 'مستخدم'}</h3>
                      <span className="post-date">
                        {new Date(post.created_at).toLocaleDateString('ar-EG')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="post-content">
                  <p>{post.content}</p>
                  {post.image_url && (
                    <div className="post-images">
                      <img src={getStorageUrl(post.image_url)} alt="Post image" />
                    </div>
                  )}
                  {post.images && post.images.length > 0 && (
                    <div className="post-images">
                      {post.images.map((img, idx) => (
                        <img key={idx} src={getStorageUrl(img)} alt={`Post ${idx + 1}`} />
                      ))}
                    </div>
                  )}
                </div>
                <div className="post-footer">
                  <button
                    className={`post-action ${isLiked ? 'liked' : ''}`}
                    onClick={() => handleReact(post.id)}
                    disabled={reactMutation.isPending}
                    style={isLiked ? { color: '#ef4444' } : {}}
                  >
                    {isLiked ? (
                      <AiFillHeart style={{ color: '#ef4444', fontSize: '18px' }} />
                    ) : (
                      <FiHeart style={{ fontSize: '18px' }} />
                    )}
                    <span>{post.likes_count || post.reactions_count || 0}</span>
                  </button>
                  <Link to={`/community/posts/${post.id}`} className="post-action">
                    <FiMessageCircle />
                    <span>{post.comments_count || 0}</span>
                  </Link>
                  <button className="post-action">
                    <FiShare2 />
                  </button>
                </div>
              </Card>
              )
            })}
            {posts.length === 0 && (
              <div className="empty-state">
                <p>لا توجد منشورات</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Community

